import { useEffect, useState } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import {
  ConfigBinaryProperty,
  ConfigCheckboxProperty,
  ConfigInputProperty,
  ConfigSection,
} from '../../../settings/components/config-property';
import {
  DeliveryConfig,
  DeliveryConfigType,
  ProductDeliveryConfig,
} from '../../../../clients/quipli-service/delivery/delivery';
import Icon from '../../../../components/icons/icon';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import { Spinner } from '../../../../components/spinner/spinner';
import { QuipliLink } from '../../../../components/link/quipli-link';
import { Typography } from '../../../../components/typography/typography';

export const ProductDeliveryConfigForm = ({ productId }: { productId?: number }) => {
  const { getDeliveryConfiguration, getProductDeliveryConfig } = useAPI();

  const [globalConf, setGlobalConf] = useState<DeliveryConfig>();
  const [productConf, setProductConf] = useState<ProductDeliveryConfig>();

  useEffect(() => {
    getDeliveryConfiguration().then((conf) => {
      setGlobalConf(conf);

      if (productId) {
        getProductDeliveryConfig(productId).then(setProductConf);
      } else {
        // Set default product config for new product
        setProductConf({
          productId: 0,
          allowShipping: true,
          allowPickup: true,
          forceWillCall: false,
          flatRate: conf.flatRate,
          useGoogleCalculation: conf.useGoogleCalculation,
          googleCalculation: conf.googleCalculation,
        });
      }
    });
  }, [productId]);

  return !productConf || !globalConf ? (
    <Spinner />
  ) : (
    <ConfigSection>
      <ConfigCheckboxProperty
        property="Delivery Available"
        description="Check box to make delivery available for product."
        name="allowShipping"
        value={productConf.allowShipping}
        onChange={(value) => setProductConf((prev) => prev && { ...productConf, allowShipping: value })}
      />

      <ConfigCheckboxProperty
        property="Pick Up In Store"
        description="Check box to make pick up in store available for product."
        name="allowPickup"
        value={productConf.allowPickup}
        onChange={(value) => setProductConf((prev) => prev && { ...productConf, allowPickup: value })}
      />

      {productConf.allowShipping && (
        <>
          {[DeliveryConfigType.Global, DeliveryConfigType.PerProduct].includes(globalConf.type) &&
            globalConf.allowWillCallAtProductLevel && (
              <ConfigBinaryProperty
                property="Free Text Delivery Option"
                name="forceWillCall"
                helper={
                  <Tooltip
                    tooltip={
                      <QuipliLink to="labels?keys=L_DELIVERY_WILL_CALL" target="_blank">
                        <div className="p-[12px]">
                          <Typography type="text-sm" className="!text-quipli-white-50 underline">
                            AKA "Will Call". Click to change label.
                          </Typography>
                        </div>
                      </QuipliLink>
                    }
                    type="black"
                  >
                    <Icon icon="InfoCircle" stroke="var(--link-100)" />
                  </Tooltip>
                }
                value={productConf.forceWillCall}
                onChange={(value) => setProductConf((prev) => prev && { ...prev, forceWillCall: value })}
              />
            )}
        </>
      )}

      {productConf.allowShipping && globalConf.type === DeliveryConfigType.PerProduct && (
        <>
          {productConf.useGoogleCalculation ? (
            <>
              <ConfigInputProperty
                property="Delivery Base Cost"
                description="Define the base cost for a one way loaded trip. If this is 0, global configuration will be used."
                name="baseCost"
                type="number"
                value={productConf.googleCalculation.baseCost}
                onChange={(value: number) =>
                  setProductConf(
                    (prev) =>
                      prev && {
                        ...prev,
                        googleCalculation: { ...prev.googleCalculation, baseCost: value >= 0 ? value : 0 },
                      },
                  )
                }
              />
              <ConfigInputProperty
                property={`${productConf.googleCalculation.measure}s included in Delivery Base Cost`}
                description={`Define the ${
                  productConf.googleCalculation.measure === 'Mile' ? 'mileage' : productConf.googleCalculation.measure
                } radius (from your store location) included in the base cost.`}
                name="includedUnits"
                type="number"
                value={productConf.googleCalculation.includedUnits}
                onChange={(value: number) =>
                  setProductConf(
                    (prev) =>
                      prev && {
                        ...prev,
                        googleCalculation: { ...prev.googleCalculation, includedUnits: value >= 0 ? value : 0 },
                      },
                  )
                }
              />
              <ConfigInputProperty
                property={`Per ${productConf.googleCalculation.measure} Cost`}
                description={`Define the additional cost per one way loaded ${productConf.googleCalculation.measure.toLocaleLowerCase()} for deliveries beyond the base cost radius.`}
                name="perUnitCost"
                type="number"
                value={productConf.googleCalculation.perUnitCost}
                onChange={(value: number) =>
                  setProductConf(
                    (prev) =>
                      prev && {
                        ...prev,
                        googleCalculation: { ...prev.googleCalculation, perUnitCost: value >= 0 ? value : 0 },
                      },
                  )
                }
              />
            </>
          ) : (
            <ConfigInputProperty
              property="Flat Delivery Charge"
              description="Define the flat rate for a one way trip (this amount will be doubled for round trip deliveries). If this is 0, global configuration will be used"
              name="rate"
              type="number"
              value={productConf.flatRate}
              onChange={(value: number) =>
                setProductConf((prev) => prev && { ...prev, flatRate: value >= 0 ? value : 0 })
              }
            />
          )}
        </>
      )}
    </ConfigSection>
  );
};
